import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { BaseComponent } from '@shared/classes';
import { CarouselItemComponent } from '@shared/components/carousel-item/carousel-item.component';
import { DeviceType } from '@shared/constants';
import { SwipeDirective } from '@shared/directives';
import { Carousel, CarouselStepContent, SwipePayload } from '@shared/models';
import { VariableReplacementPipe } from '@shared/pipes';

const ANIMATION_DURATION = 400; // ms, must match CSS transition

const SLOT_PERCENT_SMALL = 85; // mobile: 81% width + 2% margin each side
const SLOT_PERCENT_MEDIUM = 53; // tablet: 50% width + 1.5% margin each side

@Component({
  selector: 'b2c-default',
  styleUrl: './default.component.scss',
  templateUrl: './default.component.html',
  host: {
    '(window:resize)': 'onResize()',
  },
  imports: [SwipeDirective, CarouselItemComponent, VariableReplacementPipe],
})
export class DefaultSliderComponent
  extends BaseComponent
  implements AfterViewInit, OnDestroy
{
  private static nextId = 0;
  protected readonly instanceId = DefaultSliderComponent.nextId++;

  items = input.required<CarouselStepContent[]>();

  activeSlide = signal(0);
  carousel = viewChild.required<ElementRef<HTMLUListElement>>('carousel');

  config: Carousel = this.loyaltyChallengeConfig.components.tutorial.carousel;

  protected readonly isLooping: boolean = this.config.config.loop ?? true;

  private isAnimating = false;
  private animationTimer: ReturnType<typeof setTimeout> | null = null;
  private slideWidthPercent = SLOT_PERCENT_SMALL;
  private slideCount = computed(() => this.items().length);
  private centerOffsetPercent = (100 - SLOT_PERCENT_SMALL) / 2;
  // Looping prepends a clone of the last slide, which shifts every DOM position by one.
  private readonly leadingCloneCount = this.isLooping ? 1 : 0;

  protected readonly canGoPrev = computed(
    () => this.isLooping || this.activeSlide() > 0
  );
  protected readonly canGoNext = computed(
    () => this.isLooping || this.activeSlide() < this.slideCount() - 1
  );

  ngAfterViewInit(): void {
    this.updateDimensions();
    this.setPosition(0, false);
  }

  ngOnDestroy(): void {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }
  }

  onResize(): void {
    this.updateDimensions();
    this.setPosition(this.activeSlide(), false);
  }

  panStart(): void {
    if (this.isAnimating) return;
    const element = this.carousel().nativeElement;
    element.classList.remove('is-animating');
  }

  panMove(e: SwipePayload): void {
    if (this.isAnimating) return;
    const element = this.carousel().nativeElement;

    // Convert drag distance to percentage
    const containerWidth = element.parentElement?.offsetWidth || 1;
    const dragPercent = this.isDragAllowed(e.deltaX)
      ? (e.deltaX / containerWidth) * 100
      : 0;

    // Current position (centered, accounting for clone offset)
    const currentOffset = this.calculateTransform(this.activeSlide());
    element.style.transform = `translateX(${currentOffset + dragPercent}%)`;
  }

  panEnd(e: SwipePayload): void {
    if (this.isAnimating) return;
    const v = e.velocityX;
    if (v > 1 && this.isDragAllowed(v)) {
      this.goToPrevSlide();
    } else if (v < -1 && this.isDragAllowed(v)) {
      this.goToNextSlide();
    } else {
      this.setPosition(this.activeSlide(), true);
    }
  }

  goToSlideByIndex(index: number): void {
    if (this.isAnimating) return;
    const count = this.slideCount();
    // Clamp to valid range
    const next = Math.max(0, Math.min(index, count - 1));
    this.activeSlide.set(next);
    this.setPosition(next, true);
  }

  goToPrevSlide(): void {
    if (this.isAnimating || !this.canGoPrev()) return;
    const count = this.slideCount();
    const current = this.activeSlide();

    if (current === 0) {
      // At first slide: animate to clone (position -1), then jump to last
      this.animateToClone(-1, count - 1);
    } else {
      this.activeSlide.set(current - 1);
      this.setPosition(current - 1, true);
    }
  }

  goToNextSlide(): void {
    if (this.isAnimating || !this.canGoNext()) return;
    const count = this.slideCount();
    const current = this.activeSlide();

    if (current === count - 1) {
      // At last slide: animate to clone (position count), then jump to first
      this.animateToClone(count, 0);
    } else {
      this.activeSlide.set(current + 1);
      this.setPosition(current + 1, true);
    }
  }

  /**
   * Update slide dimensions based on device type.
   * This component only renders for MOBILE and TABLET (parent destroys it on DESKTOP).
   */
  private updateDimensions(): void {
    if (this.device() === DeviceType.TABLET) {
      this.slideWidthPercent = SLOT_PERCENT_MEDIUM;
    } else {
      this.slideWidthPercent = SLOT_PERCENT_SMALL;
    }
    this.centerOffsetPercent = (100 - this.slideWidthPercent) / 2;
  }

  /** A positive delta drags towards the previous slide, a negative one towards the next. */
  private isDragAllowed(deltaX: number): boolean {
    return deltaX > 0 ? this.canGoPrev() : this.canGoNext();
  }

  /**
   * Calculate transform percentage for a given logical slide index.
   * Looping DOM structure: [clone of last] [real slides...] [clone of first]
   */
  private calculateTransform(logicalIndex: number): number {
    const domPosition = logicalIndex + this.leadingCloneCount;
    return this.centerOffsetPercent - this.slideWidthPercent * domPosition;
  }

  private setPosition(logicalIndex: number, animate: boolean): void {
    const el = this.carousel().nativeElement;
    if (animate) {
      el.classList.add('is-animating');
    } else {
      el.classList.remove('is-animating');
    }
    const percentage = this.calculateTransform(logicalIndex);
    el.style.transform = `translateX(${percentage}%)`;
  }

  /**
   * Animate to a clone position, then instantly jump to the real slide.
   * clonePosition: -1 for clone of last, count for clone of first
   * targetIndex: the real slide index to jump to after animation
   */
  private animateToClone(clonePosition: number, targetIndex: number): void {
    this.isAnimating = true;
    const el = this.carousel().nativeElement;

    const domPosition = clonePosition + this.leadingCloneCount;
    const percentage =
      this.centerOffsetPercent - this.slideWidthPercent * domPosition;

    el.classList.add('is-animating');
    el.style.transform = `translateX(${percentage}%)`;

    // After animation, instantly jump to the real slide
    this.animationTimer = setTimeout(() => {
      this.activeSlide.set(targetIndex);
      this.setPosition(targetIndex, false);
      this.isAnimating = false;
      this.animationTimer = null;
    }, ANIMATION_DURATION);
  }
}
